import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const BOIDS_PARAMS = [
  {
    kind: 'int',
    key: 'count',
    label: 'Schwarmgrösse',
    min: 50,
    max: 20000,
    step: 1,
    scale: 'log',
    default: 2200,
    bulkRange: [400, 6000],
    group: 'Schwarm',
  },
  {
    kind: 'int',
    key: 'steps',
    label: 'Simulationsschritte',
    min: 10,
    max: 3000,
    step: 10,
    default: 380,
    bulkRange: [80, 1200],
    group: 'Schwarm',
    tooltip: 'Wie lange der Schwarm läuft, bevor das Standbild steht.',
  },
  {
    kind: 'float',
    key: 'maxSpeed',
    label: 'Max. Geschwindigkeit',
    min: 0.2,
    max: 14,
    step: 0.1,
    default: 3,
    unit: 'px',
    group: 'Schwarm',
  },
  {
    kind: 'float',
    key: 'maxForce',
    label: 'Max. Kraft',
    min: 0.005,
    max: 1,
    step: 0.005,
    default: 0.12,
    group: 'Schwarm',
  },
  {
    kind: 'float',
    key: 'viewAngle',
    label: 'Sichtwinkel',
    min: 30,
    max: 360,
    step: 5,
    default: 260,
    unit: '°',
    group: 'Schwarm',
    tooltip: 'Nachbarn ausserhalb dieses Kegels werden ignoriert.',
  },

  {
    kind: 'float',
    key: 'separationWeight',
    label: 'Separation',
    min: 0,
    max: 4,
    step: 0.05,
    default: 1.6,
    group: 'Regeln',
  },
  {
    kind: 'float',
    key: 'separationRadius',
    label: 'Separationsradius',
    min: 2,
    max: 120,
    step: 1,
    default: 16,
    unit: 'px',
    group: 'Regeln',
  },
  {
    kind: 'float',
    key: 'alignmentWeight',
    label: 'Alignment',
    min: 0,
    max: 4,
    step: 0.05,
    default: 1,
    group: 'Regeln',
  },
  {
    kind: 'float',
    key: 'alignmentRadius',
    label: 'Alignment-Radius',
    min: 2,
    max: 200,
    step: 1,
    default: 42,
    unit: 'px',
    group: 'Regeln',
  },
  {
    kind: 'float',
    key: 'cohesionWeight',
    label: 'Cohesion',
    min: 0,
    max: 4,
    step: 0.05,
    default: 0.85,
    group: 'Regeln',
  },
  {
    kind: 'float',
    key: 'cohesionRadius',
    label: 'Cohesion-Radius',
    min: 2,
    max: 260,
    step: 1,
    default: 58,
    unit: 'px',
    group: 'Regeln',
  },

  {
    kind: 'enum',
    key: 'textMode',
    label: 'Textbezug',
    default: 'shape',
    group: 'Text',
    options: [
      { value: 'shape', label: 'Kontur formen' },
      { value: 'orbit', label: 'Umkreisen' },
      { value: 'avoid', label: 'Meiden' },
      { value: 'none', label: 'Ignorieren' },
    ],
  },
  {
    kind: 'float',
    key: 'textWeight',
    label: 'Textkraft',
    min: 0,
    max: 6,
    step: 0.05,
    default: 2.2,
    group: 'Text',
    showIf: (v) => v.textMode !== 'none',
  },

  {
    kind: 'enum',
    key: 'display',
    label: 'Darstellung',
    default: 'trail',
    group: 'Darstellung',
    options: [
      { value: 'dot', label: 'Punkt' },
      { value: 'triangle', label: 'Dreieck' },
      { value: 'line', label: 'Linie' },
      { value: 'trail', label: 'Spur' },
    ],
  },
  {
    kind: 'int',
    key: 'trailLength',
    label: 'Spurlänge',
    min: 2,
    max: 200,
    step: 1,
    default: 26,
    group: 'Darstellung',
    showIf: (v) => v.display === 'trail',
  },
  {
    kind: 'float',
    key: 'size',
    label: 'Grösse',
    min: 0.3,
    max: 24,
    step: 0.1,
    default: 2.6,
    unit: 'px',
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.02,
    max: 1,
    step: 0.01,
    default: 0.5,
    group: 'Darstellung',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'speed',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'speed', label: 'Nach Tempo' },
      { value: 'heading', label: 'Nach Richtung' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'neon' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type BoidsValues = SchemaValues<typeof BOIDS_PARAMS>;
